// Small shared helpers used by all three portal sidebars
// (EnvironmentalPortalSidebar, LcpPortalSidebar, SitePortalSidebar) for
// the bottom org/user card:
//
//   - formatOrgName(orgName, userDetails) → appends "(Enterprise)" when the
//     caller's org is itself an enterprise tenant, or "(Managed)" when this
//     org is a child of an enterprise. Both flags come from userDetails;
//     null-safe so the legacy `orgName` string falls through unchanged
//     until the backend wires up the new fields.
//
//   - <RolesRow userDetails /> → row of MUI <Chip>s under the user's name.
//     First chip is the primary role (OWNER / SUPER_ADMIN / etc.) in a
//     distinct accent color; remaining chips are secondary scopes the
//     org admin assigned the user (Technician, Service Manager, AP, …)
//     read from `userDetails.secondaryRoles` (string[]). Empty array
//     means just the primary chip renders.
//
// Backend is responsible for surfacing `isEnterpriseOrganization`,
// `isManagedByEnterprise`, and `secondaryRoles` on UserDetailsDto. Until
// those land, the helpers are silent: org-name suffix is dropped and
// only the primary-role chip renders. No conditional imports — both
// helpers are pure presentation.

import React from "react";
import PropTypes from "prop-types";
import { Box, Chip, Stack, Tooltip } from "@mui/material";

// Email synthesized server-side when the operator creates a user with
// only a username. We never want to display these. Used by the sidebar
// + anywhere else that falls back to email for display.
export const SYNTHETIC_EMAIL_DOMAIN = "@no-email.flex2.local";
export const isSyntheticEmail = (e) =>
  typeof e === "string" && e.toLowerCase().endsWith(SYNTHETIC_EMAIL_DOMAIN);

// Pretty-print known role identifiers; pass-through anything we don't
// recognize (so a future server role still shows up readably).
const ROLE_LABELS = {
  SUPER_ADMIN: "Super Admin",
  OWNER: "Owner",
  ADMIN: "Admin",
  USER: "User",
  RECYCLING_VENDOR: "Recycling Vendor",
};

const prettyRole = (raw) => {
  if (!raw) return null;
  if (ROLE_LABELS[raw]) return ROLE_LABELS[raw];
  // CamelCase / underscored fallback prettifier.
  return String(raw)
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

export const formatOrgName = (orgName, userDetails) => {
  if (!orgName) return orgName;
  if (userDetails?.isEnterpriseOrganization === true) {
    return `${orgName} (Enterprise)`;
  }
  if (userDetails?.isManagedByEnterprise === true) {
    return `${orgName} (Managed)`;
  }
  return orgName;
};

export function RolesRow({ userDetails, locationByRole }) {
  const primary = prettyRole(userDetails?.role);
  // secondaryRoles may not exist yet; default to []. Keep the raw scope
  // kind alongside the pretty label so we can look the role's default
  // location up in `locationByRole` (keyed by scopeKind). Filter falsy +
  // dedupe against the primary role so the same label doesn't double-up
  // (e.g. an OWNER who also has the "Owner" sub-role flag).
  const secondary = Array.isArray(userDetails?.secondaryRoles)
    ? userDetails.secondaryRoles
        .map((kind) => ({ kind, label: prettyRole(kind) }))
        .filter(({ label }) => label && label !== primary)
    : [];

  if (!primary && secondary.length === 0) return null;

  return (
    <Box sx={{ mt: 0.75 }}>
      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
        {primary && (
          <Chip
            label={primary}
            size="small"
            sx={{
              // Accent color — gold/amber on dark bg so the primary
              // role stands out from secondary scopes.
              height: 20,
              bgcolor: "rgba(255,193,7,0.18)",
              color: "#ffe082",
              border: "1px solid rgba(255,193,7,0.45)",
              fontSize: 10,
              fontWeight: 800,
              letterSpacing: 0.2,
              "& .MuiChip-label": { px: 0.75 },
            }}
          />
        )}
        {secondary.map(({ kind, label }) => {
          const loc = locationByRole?.[kind];
          const chip = (
            <Chip
              label={label}
              size="small"
              sx={{
                height: 20,
                bgcolor: "rgba(255,255,255,0.08)",
                color: "rgba(255,255,255,0.82)",
                border: "1px solid rgba(255,255,255,0.18)",
                fontSize: 10,
                fontWeight: 700,
                "& .MuiChip-label": { px: 0.75 },
              }}
            />
          );
          // Only surface a tooltip when this role actually has a default
          // location set, per the request.
          return loc ? (
            <Tooltip key={label} arrow title={`Default location: ${loc}`}>
              {chip}
            </Tooltip>
          ) : (
            <React.Fragment key={label}>{chip}</React.Fragment>
          );
        })}
      </Stack>
    </Box>
  );
}

RolesRow.propTypes = {
  userDetails: PropTypes.shape({
    role: PropTypes.string,
    secondaryRoles: PropTypes.arrayOf(PropTypes.string),
  }),
  // Map of scopeKind → default location name, used for the per-role
  // hover tooltip. Optional; absent → no tooltips.
  locationByRole: PropTypes.objectOf(PropTypes.string),
};

RolesRow.defaultProps = {
  locationByRole: {},
};
